"use client";

import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SessionDetail } from "@/components/session-detail";

type NeighborId = string | null | undefined;

interface SessionDetailModalProps {
  open: boolean;
  sessionId: string | null;
  onFetchNeighbor: (direction: "prev" | "next") => Promise<string | null>;
  onClose: () => void;
  onNavigate: (id: string) => void;
}

export function SessionDetailModal({
  open,
  sessionId,
  onFetchNeighbor,
  onClose,
  onNavigate,
}: SessionDetailModalProps) {
  const [prevId, setPrevId] = useState<NeighborId>(undefined);
  const [nextId, setNextId] = useState<NeighborId>(undefined);

  // Render-phase reset avoids the double-render of an effect.
  const currentKey = open && sessionId ? sessionId : "";
  const [prevKey, setPrevKey] = useState(currentKey);
  if (prevKey !== currentKey) {
    setPrevKey(currentKey);
    setPrevId(undefined);
    setNextId(undefined);
  }

  useEffect(() => {
    if (!open || !sessionId) return;
    let cancelled = false;
    onFetchNeighbor("prev").then((id) => {
      if (!cancelled) setPrevId(id);
    });
    onFetchNeighbor("next").then((id) => {
      if (!cancelled) setNextId(id);
    });
    return () => {
      cancelled = true;
    };
  }, [open, sessionId, onFetchNeighbor]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft" && prevId) {
        e.preventDefault();
        onNavigate(prevId);
      } else if (e.key === "ArrowRight" && nextId) {
        e.preventDefault();
        onNavigate(nextId);
      } else if (e.key === "Escape") {
        onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, prevId, nextId, onNavigate, onClose]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="max-w-6xl w-[90vw] h-[85vh] p-0 flex flex-col gap-0 sm:max-w-6xl"
      >
        <DialogTitle className="sr-only">会话详情</DialogTitle>
        <div className="flex items-center gap-2 border-b px-4 py-2">
          <Button
            variant="outline"
            size="sm"
            disabled={prevId == null}
            onClick={() => prevId && onNavigate(prevId)}
          >
            ◀ 上一条
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={nextId == null}
            onClick={() => nextId && onNavigate(nextId)}
          >
            下一条 ▶
          </Button>
          <div className="ml-auto flex items-center gap-2">
            {sessionId && (
              <Button asChild variant="outline" size="sm">
                <a href={`/history/${sessionId}`} target="_blank" rel="noopener noreferrer">
                  打开完整页面 ↗
                </a>
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={onClose}>
              关闭
            </Button>
          </div>
        </div>
        {sessionId && <SessionDetail key={sessionId} sessionId={sessionId} readOnly />}
      </DialogContent>
    </Dialog>
  );
}
